import { SortDirection } from 'Components/Table/SortableTableHeader/SortableTableHeader';
import { toTitleCase } from 'Helpers/StringUtils';
import { compareUsersBySubjectForSorting } from 'Helpers/UserUtils';
import { FileToBeUploaded, FileUpdates, UploadedFile } from 'Models/Files';
import { OperationalControl } from 'Models/OperationalControls';
import { thirdPartyNameComparator } from 'Models/TPRM';
import { GroupOptionType, OptionType } from 'Models/Types/GlobalType';
import { UserResponse } from 'Models/User';

import { IssuesExceptionsModule } from './Issues';
import { DisplayableTag } from './Tags';

/**
 * This represents an Exception. The "-Entity" suffix is used to avoid shadowing Python's built-in `Exception` type.
 * Create and update requests write this to the database.
 * This is not sent in responses. Instead, it is converted to an ExceptionResponse DTO, since the frontend needs to know:
 * 1. Whether the Exception belongs to the Operational Controls module or the TPRM module.
 * 2. Additional details about the controls that an Exception is mapped to, if it belongs in the Operational Controls module.
 */
export interface ExceptionEntity {
    id: string;
    status: ExceptionStatus;
    risk_score: ExceptionRiskScore; // Calculated based on impact and likelihood.
    created_timestamp: string;
    title: string;
    expiration_date: string;
    description: string;
    likelihood: ExceptionLikelihood;
    impact: ExceptionImpact;
    remediation_plan: string;
    reviewer_subject: string;
    owner_subject: string;
    risk_assessment: string;
    created_by: string;
    last_updated_by: string;
    last_updated_timestamp: string;

    delegates: string[];
    reference?: string;
    impacted_controls: string[];
    impacted_vendor?: string;
    compensating_controls?: string;
    files: UploadedFile[];
    tags: string[];

    // These fields are always present when the issue is CLOSED.
    closure_statement?: string;
    closed_timestamp?: string;
    closed_by?: string;
}

/** A request to create a new Exception. */
export interface CreateExceptionRequest {
    title: string;
    expiration_date: string;
    description: string;
    likelihood: ExceptionLikelihood;
    impact: ExceptionImpact;
    remediation_plan: string;
    reviewer_subject: string;
    owner_subject: string;
    risk_assessment: string;

    delegates: string[];
    reference?: string;
    compensating_controls?: string;
    impacted_controls: string[];
    impacted_vendor?: string;
    files: FileToBeUploaded[];
    tags: string[];
}

/** A request to update an existing Exception. */
export interface UpdateExceptionRequest {
    title: string;
    status: ExceptionStatus;
    expiration_date: string;
    description: string;
    likelihood: ExceptionLikelihood;
    impact: ExceptionImpact;
    remediation_plan: string;
    reviewer_subject: string;
    owner_subject: string;
    risk_assessment: string;

    delegates: string[];
    reference?: string;
    impacted_controls: string[];
    impacted_vendor?: string;
    compensating_controls?: string;
    file_updates: FileUpdates;
    tags: string[];

    closure_statement?: string;
}

/**
 * A DTO used to make Exception presentable to front-end clients. In short, the only differences between an instance of this class and the `ExceptionEntity` it's derived from are:
 * 1. It provides "full" details for the Controls impacted by the Exception, rather than just the Control IDs.
 * 2. It identifies itself as belonging to the Operational Controls module.
 */
export type ControlExceptionResponse = Omit<ExceptionEntity, 'impacted_controls'> & { type: IssuesExceptionsModule.CONTROLS; impacted_controls: OperationalControl[] };

/**
 * A DTO used to make Exception presentable to front-end clients. In short, the only difference between an instance of this class and the `ExceptionEntity` it's derived from is:
 * 1. It identifies itself as belonging to the TPRM module.
 */
export type ThirdPartyExceptionResponse = ExceptionEntity & { type: IssuesExceptionsModule.TPRM };

/** A DTO used to make ExceptionEntity presentable to front-end clients. */
export type ExceptionResponse = ControlExceptionResponse | ThirdPartyExceptionResponse;

/** This object defines changes to an ExceptionEntity's attributes that are necessary when the ExceptionEntity is saved as a history record. */
export type ExceptionHistoryModifications = Omit<ExceptionEntity, 'tags'> & { tags: DisplayableTag[] };

/**
 * A DTO used to make an ExceptionHistoryModifications presentable to front-end clients. In short, the only differences between an instance of this class and the `ExceptionHistoryModifications` it's derived from are:
 * 1. It provides "full" details for the Controls impacted by the ExceptionHistoryModifications, rather than just the Control IDs.
 * 2. It identifies itself as belonging to the Operational Controls module.
 */
export type ControlExceptionHistoryResponse = Omit<ExceptionHistoryModifications, 'impacted_controls'> & { type: IssuesExceptionsModule.CONTROLS; impacted_controls: OperationalControl[] };

/**
 * A DTO used to make an ExceptionHistoryModifications presentable to front-end clients. In short, the only difference between an instance of this class and the `ExceptionHistoryModifications` it's derived from is:
 * 1. It identifies itself as belonging to the TPRM module.
 */
export type ThirdPartyExceptionHistoryResponse = ExceptionHistoryModifications & { type: IssuesExceptionsModule.TPRM };

/** A DTO used to make IssueHistory presentable to front-end clients. */
export interface ExceptionHistoryResponse {
    timestamp: string;
    exception: ControlExceptionHistoryResponse | ThirdPartyExceptionHistoryResponse;
}

/**
 * A DTO used to make an ExceptionEntity presentable to front-end clients for the Export feature.
 * The difference is the Export version of an Issue needs UI-friendly versions of Controls or Third-Party names, depending on which feature the Issue belongs to.
 */
export interface ExceptionExportResponse extends Omit<ExceptionEntity, 'impacted_controls' | 'impacted_vendor'> {
    impacted_controls: OperationalControl[];
    impacted_vendor?: string; // Technically this is the same data type as defined in the ExceptionEntity interface, but it's listed here in order to highlight that this is the Third-Party's name instead of their UUID.
}

export enum ExceptionsSortFilterOptions {
    TITLE = 'title',
    EXPIRATION = 'expiration_date',
    DESCRIPTION = 'description',
    OWNER = 'owner_subject',
    REVIEWER = 'reviewer_subject',
    STATUS = 'status',
    RISK_SCORE = 'risk_score',
    CONTROLS = 'controls',
    THIRD_PARTY = 'impacted_vendor',
    TAGS = 'tags',
}

export enum ExceptionStatus {
    DRAFT_OPEN = 'DRAFT_OPEN',
    APPROVED = 'APPROVED',
    DRAFT_CLOSE = 'DRAFT_CLOSE',
    CLOSED = 'CLOSED',
}

const statusAsNumber = (status: ExceptionStatus): number => {
    switch (status) {
        case ExceptionStatus.DRAFT_OPEN:
            return 0;
        case ExceptionStatus.APPROVED:
            return 1;
        case ExceptionStatus.DRAFT_CLOSE:
            return 2;
        case ExceptionStatus.CLOSED:
            return 3;
    }
};

export const statusComparator = (statusA: ExceptionStatus, statusB: ExceptionStatus): number => {
    if (statusA === statusB) {
        return 0;
    } else {
        const valueA = statusAsNumber(statusA);
        const valueB = statusAsNumber(statusB);
        return valueA < valueB ? -1 : 1;
    }
};

export const ExceptionStatusFilterOptionTypes: GroupOptionType[] = [
    {
        groupId: ExceptionsSortFilterOptions.STATUS,
        label: 'Draft Open',
        value: ExceptionStatus.DRAFT_OPEN,
    },
    {
        groupId: ExceptionsSortFilterOptions.STATUS,
        label: 'Approved',
        value: ExceptionStatus.APPROVED,
    },
    {
        groupId: ExceptionsSortFilterOptions.STATUS,
        label: 'Draft Close',
        value: ExceptionStatus.DRAFT_CLOSE,
    },
    {
        groupId: ExceptionsSortFilterOptions.STATUS,
        label: 'Closed',
        value: ExceptionStatus.CLOSED,
    },
];

export enum ExceptionLikelihood {
    RARE = 'RARE',
    UNLIKELY = 'UNLIKELY',
    POSSIBLE = 'POSSIBLE',
    LIKELY = 'LIKELY',
    ALMOST_CERTAIN = 'ALMOST_CERTAIN',
}

export const ExceptionLikelihoodOptions: OptionType[] = [
    {
        value: ExceptionLikelihood.RARE,
        label: toTitleCase(ExceptionLikelihood.RARE),
    },
    {
        value: ExceptionLikelihood.UNLIKELY,
        label: toTitleCase(ExceptionLikelihood.UNLIKELY),
    },
    {
        value: ExceptionLikelihood.POSSIBLE,
        label: toTitleCase(ExceptionLikelihood.POSSIBLE),
    },
    {
        value: ExceptionLikelihood.LIKELY,
        label: toTitleCase(ExceptionLikelihood.LIKELY),
    },
    {
        value: ExceptionLikelihood.ALMOST_CERTAIN,
        label: toTitleCase(ExceptionLikelihood.ALMOST_CERTAIN),
    },
];

export enum ExceptionImpact {
    LOW = 'LOW',
    LOW_MODERATE = 'LOW_MODERATE',
    MODERATE = 'MODERATE',
    MODERATE_HIGH = 'MODERATE_HIGH',
    HIGH = 'HIGH',
}

export const ExceptionImpactOptions: OptionType[] = [
    {
        value: ExceptionImpact.LOW,
        label: toTitleCase(ExceptionImpact.LOW, '/'),
    },
    {
        value: ExceptionImpact.LOW_MODERATE,
        label: toTitleCase(ExceptionImpact.LOW_MODERATE, '/'),
    },
    {
        value: ExceptionImpact.MODERATE,
        label: toTitleCase(ExceptionImpact.MODERATE, '/'),
    },
    {
        value: ExceptionImpact.MODERATE_HIGH,
        label: toTitleCase(ExceptionImpact.MODERATE_HIGH, '/'),
    },
    {
        value: ExceptionImpact.HIGH,
        label: toTitleCase(ExceptionImpact.HIGH, '/'),
    },
];

export enum ExceptionRiskScore {
    LOW = 'LOW',
    LOW_MODERATE = 'LOW_MODERATE',
    MODERATE = 'MODERATE',
    MODERATE_HIGH = 'MODERATE_HIGH',
    HIGH = 'HIGH',
}

export const ExceptionRiskScoreFilterOptionTypes: GroupOptionType[] = [
    {
        groupId: ExceptionsSortFilterOptions.RISK_SCORE,
        label: 'Low',
        value: ExceptionRiskScore.LOW,
    },
    {
        groupId: ExceptionsSortFilterOptions.RISK_SCORE,
        label: 'Low/Moderate',
        value: ExceptionRiskScore.LOW_MODERATE,
    },
    {
        groupId: ExceptionsSortFilterOptions.RISK_SCORE,
        label: 'Moderate',
        value: ExceptionRiskScore.MODERATE,
    },
    {
        groupId: ExceptionsSortFilterOptions.RISK_SCORE,
        label: 'Moderate/High',
        value: ExceptionRiskScore.MODERATE_HIGH,
    },
    {
        groupId: ExceptionsSortFilterOptions.RISK_SCORE,
        label: 'High',
        value: ExceptionRiskScore.HIGH,
    },
];

const riskScoreAsNumber = (score: ExceptionRiskScore): number => {
    switch (score) {
        case ExceptionRiskScore.LOW:
            return 0;
        case ExceptionRiskScore.LOW_MODERATE:
            return 1;
        case ExceptionRiskScore.MODERATE:
            return 2;
        case ExceptionRiskScore.MODERATE_HIGH:
            return 3;
        case ExceptionRiskScore.HIGH:
            return 4;
    }
};

export const riskScoreComparator = (scoreA: ExceptionRiskScore, scoreB: ExceptionRiskScore): number => {
    if (scoreA === scoreB) {
        return 0;
    } else {
        const valueA = riskScoreAsNumber(scoreA);
        const valueB = riskScoreAsNumber(scoreB);
        return valueA < valueB ? 1 : -1;
    }
};

export enum ExceptionRiskScoreFilter {
    ALL = 'ALL',
    LOW = 'LOW',
    LOW_MODERATE = 'LOW_MODERATE',
    MODERATE = 'MODERATE',
    MODERATE_HIGH = 'MODERATE_HIGH',
    HIGH = 'HIGH',
}

export const isValidExceptionRiskScoreFilter = (value: unknown): value is ExceptionRiskScoreFilter => typeof value === 'string' && Object.values(ExceptionRiskScoreFilter).includes(value as ExceptionRiskScoreFilter);

export const ExceptionRiskScoreFilterOptions: OptionType[] = [
    {
        value: ExceptionRiskScoreFilter.ALL,
        label: 'All Risk Scores',
    },
    {
        value: ExceptionRiskScoreFilter.LOW,
        label: toTitleCase(ExceptionRiskScoreFilter.LOW, '/'),
    },
    {
        value: ExceptionRiskScoreFilter.LOW_MODERATE,
        label: toTitleCase(ExceptionRiskScoreFilter.LOW_MODERATE, '/'),
    },
    {
        value: ExceptionRiskScoreFilter.MODERATE,
        label: toTitleCase(ExceptionRiskScoreFilter.MODERATE, '/'),
    },
    {
        value: ExceptionRiskScoreFilter.MODERATE_HIGH,
        label: toTitleCase(ExceptionRiskScoreFilter.MODERATE_HIGH, '/'),
    },
    {
        value: ExceptionRiskScoreFilter.HIGH,
        label: toTitleCase(ExceptionRiskScoreFilter.HIGH, '/'),
    },
];

export const sortExceptions = (exceptions: ExceptionResponse[], users: UserResponse[], sortBy: ExceptionsSortFilterOptions, sortDirection: SortDirection, thirdPartyIdToNameMap: Map<string, string> = new Map([])): void => {
    exceptions.sort((exceptionA, exceptionB) => {
        let sortResult = 0;

        switch (sortBy) {
            case ExceptionsSortFilterOptions.OWNER:
                sortResult = compareUsersBySubjectForSorting(exceptionA.owner_subject, exceptionB.owner_subject, users);
                break;
            case ExceptionsSortFilterOptions.REVIEWER:
                sortResult = compareUsersBySubjectForSorting(exceptionA.reviewer_subject, exceptionB.reviewer_subject, users);
                break;
            case ExceptionsSortFilterOptions.EXPIRATION:
                sortResult = exceptionA.expiration_date > exceptionB.expiration_date ? 1 : -1;
                break;
            case ExceptionsSortFilterOptions.STATUS:
                sortResult = statusComparator(exceptionA.status, exceptionB.status);
                break;
            case ExceptionsSortFilterOptions.THIRD_PARTY:
                if (exceptionA.type === IssuesExceptionsModule.TPRM && exceptionB.type === IssuesExceptionsModule.TPRM) {
                    sortResult = thirdPartyNameComparator(exceptionA.impacted_vendor!, exceptionB.impacted_vendor!, thirdPartyIdToNameMap);
                } else {
                    throw new Error('Cannot sort Operational Controls exceptions by third party.');
                }
                break;
            case ExceptionsSortFilterOptions.CONTROLS:
                if (exceptionA.type === IssuesExceptionsModule.CONTROLS && exceptionB.type === IssuesExceptionsModule.CONTROLS) {
                    sortResult = exceptionA.impacted_controls.length - exceptionB.impacted_controls.length;
                } else {
                    throw new Error('Cannot sort TPRM exceptions by control.');
                }
                break;
            case ExceptionsSortFilterOptions.RISK_SCORE:
                sortResult = riskScoreComparator(exceptionA.risk_score, exceptionB.risk_score);
                break;
            // Can't really sort by Tags.
            case ExceptionsSortFilterOptions.TAGS:
                sortResult = 0;
                break;
            default:
                sortResult = exceptionA[sortBy].localeCompare(exceptionB[sortBy]);
                break;
        }

        return sortDirection === SortDirection.ASC ? sortResult : -sortResult;
    });
};

/**
 * This relatively simple function exists because we have previously had inconsistencies in whether drafts are considered "open".
 * @returns The number of issues in the provided list that are considered "open".
 */
export const countOpenExceptions = (exceptions: ExceptionResponse[]): number => exceptions.filter((exception) => exception.status === ExceptionStatus.APPROVED || exception.status === ExceptionStatus.DRAFT_CLOSE).length;
